import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageConfig from '@/component/layout/PageConfig';
import { useQueryGetMentalHealthReportDetail } from '@/api/mentalHealth/getMentalHealthReports';
import useQueryGetUsers from '@/api/user/getUsers';
import VitalSignsChart from '@/component/mentalHealth/VitalSignsChart';
import { SymptomPredictionCard } from '@/component/mentalHealth/SymptomPredictionCard';
import BorderButton from '@/component/button/BorderButton';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { createRoot } from 'react-dom/client';
import MentalHealthReportPDFTemplate from './MentalHealthReportPDFTemplate';
import MentalHealthReportDetailedPDFTemplate from './MentalHealthReportDetailedPDFTemplate';

const MentalHealthReportDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'symptoms' | 'charts' | 'raw'>('overview');
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: reportData, isLoading, error } = useQueryGetMentalHealthReportDetail(id);
  const { data: users } = useQueryGetUsers({});

  const report = reportData?.report;
  const user = useMemo(() => {
    if (!reportData?.userId || !users) return undefined;
    if (Array.isArray(users)) {
      return users.find(u => u.id === reportData.userId);
    }
    return undefined;
  }, [reportData?.userId, users]);

  const translateValue = (value: string | undefined, prefix?: string): string => {
    if (!value) return '';
    let translationKey: string;
    if (value.startsWith('mentalHealth.')) {
      translationKey = value;
    } else if (prefix) {
      translationKey = `${prefix}.${value}`;
    } else {
      return value;
    }
    const translated = t(translationKey);
    if (translated === translationKey) {
      return value;
    }
    return translated;
  };

  if (isLoading) {
    return (
      <PageConfig showTopNav={true} showSideNav={true}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}...</p>
          </div>
        </div>
      </PageConfig>
    );
  }

  if (error || !report) {
    return (
      <PageConfig showTopNav={true} showSideNav={true}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 mb-4">{t('common.error')}</p>
            <BorderButton text={t('mentalHealth.reports.button.dashboard')} onClick={() => navigate('/mental-health')} />
          </div>
        </div>
      </PageConfig>
    );
  }

  // 요약 다운로드 (기존 형식)
  const handleDownloadSummary = async () => {
    setShowDownloadMenu(false);
    try {
      toast.info(t('mentalHealth.reportDetail.generatingPDF'), { autoClose: false, toastId: 'pdf-gen' });

      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.top = '-10000px';
      container.style.left = '-10000px';
      document.body.appendChild(container);

      const root = createRoot(container);
      root.render(
        <MentalHealthReportPDFTemplate report={report} user={user} />
      );

      await new Promise(resolve => setTimeout(resolve, 1500));

      const element = container.querySelector('#pdf-template') as HTMLElement;
      if (!element) throw new Error('Template not found');

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: 1200
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const date = reportData?.analysisDate ? new Date(reportData.analysisDate) : new Date();
      const dateStr = date.toISOString().split('T')[0];
      const userName = reportData?.userName || 'user';
      const fileName = `health-report-summary-${userName}-${dateStr}.pdf`;

      pdf.save(fileName);

      root.unmount();
      document.body.removeChild(container);
      toast.dismiss('pdf-gen');
      toast.success(t('mentalHealth.reportDetail.downloadSuccess'));

    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.dismiss('pdf-gen');
      toast.error(t('common.error'));
    }
  };

  // 상세 다운로드 (모든 내용 포함)
  const handleDownloadDetailed = async () => {
    setShowDownloadMenu(false);
    try {
      toast.info(t('mentalHealth.reportDetail.generatingPDF'), { autoClose: false, toastId: 'pdf-gen' });

      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.top = '-10000px';
      container.style.left = '-10000px';
      document.body.appendChild(container);

      const root = createRoot(container);
      root.render(
        <MentalHealthReportDetailedPDFTemplate 
          report={report} 
          user={user} 
          analysisDate={reportData?.analysisDate}
        />
      );

      await new Promise(resolve => setTimeout(resolve, 2000));

      const element = container.querySelector('#pdf-template-detailed') as HTMLElement;
      if (!element) throw new Error('Template not found');

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: 1200
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const date = reportData?.analysisDate ? new Date(reportData.analysisDate) : new Date();
      const dateStr = date.toISOString().split('T')[0];
      const userName = reportData?.userName || 'user';
      const fileName = `health-report-detailed-${userName}-${dateStr}.pdf`;

      pdf.save(fileName);

      root.unmount();
      document.body.removeChild(container);
      toast.dismiss('pdf-gen');
      toast.success(t('mentalHealth.reportDetail.downloadSuccess'));

    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.dismiss('pdf-gen');
      toast.error(t('common.error'));
    }
  };

  const tabs = [
    { id: 'overview', label: t('mentalHealth.detail.overview'), icon: '📊' },
    { id: 'symptoms', label: t('mentalHealth.detail.symptoms'), icon: '🏥' },
    { id: 'charts', label: t('mentalHealth.detail.charts'), icon: '📈' },
    { id: 'raw', label: t('mentalHealth.detail.rawData'), icon: '🔢' },
  ];

  return (
    <PageConfig showTopNav={true} showSideNav={true}>
      <div className="p-6 space-y-6">
        {/* 헤더 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('mentalHealth.reportDetail.title')}</h1>
            <p className="text-gray-600 mt-1">{t('mentalHealth.reportDetail.subtitle')}</p>
          </div>
          <div className="flex space-x-3">
            <BorderButton text={t('mentalHealth.reportDetail.backToList')} onClick={() => navigate(-1)} />
            <div className="relative" ref={downloadMenuRef}>
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('mentalHealth.reportDetail.download')}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showDownloadMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <button
                    onClick={handleDownloadSummary}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center"
                  >
                    <span className="mr-2">📄</span>
                    {t('mentalHealth.reportDetail.downloadSummary')}
                  </button>
                  <button
                    onClick={handleDownloadDetailed}
                    className="w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 rounded-b-lg flex items-center border-t border-gray-100"
                  >
                    <span className="mr-2">📋</span>
                    {t('mentalHealth.reportDetail.downloadDetailed')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 탭 네비게이션 */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* 탭 컨텐츠 */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* 종합 건강 상태 헤더 */}
            <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 rounded-3xl p-8 border border-slate-200">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full -translate-y-32 translate-x-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-indigo-200/30 to-transparent rounded-full translate-y-24 -translate-x-24"></div>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-3xl font-bold text-slate-800 mb-2">{t('mentalHealth.reportDetail.comprehensive')}</h2>
                    <p className="text-slate-600">{t('mentalHealth.reportDetail.hrvBased')}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-500">{t('mentalHealth.reportDetail.analysisDate')}</div>
                    <div className="text-lg font-semibold text-slate-700">
                      {new Date().toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* 메인 점수 */}
                  <div className="lg:col-span-1 flex flex-col items-center">
                    <div className="relative">
                      <div className="w-40 h-40 rounded-full bg-white shadow-lg flex items-center justify-center border-8 border-slate-100">
                        <div className="text-center">
                          <div className={`text-5xl font-black mb-1 ${report.mindScore.score >= 80 ? 'text-emerald-600' :
                            report.mindScore.score >= 70 ? 'text-blue-600' :
                              report.mindScore.score >= 55 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                            {Math.round(report.mindScore.score)}
                          </div>
                          <div className="text-sm font-medium text-slate-500">/ 100{t('mentalHealth.score.points')}</div>
                        </div>
                      </div>
                      <div className={`absolute -bottom-2 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full text-sm font-bold text-white shadow-lg ${report.decisionLabel === 'healthy' ? 'bg-emerald-500' :
                        report.decisionLabel === 'normal' ? 'bg-blue-500' :
                          report.decisionLabel === 'caution' ? 'bg-amber-500' : 'bg-red-500'
                        }`}>
                        {t(`mentalHealth.status.${report.decisionLabel}`)}
                      </div>
                    </div>
                  </div>

                  {/* 핵심 지표 (개선된 디자인) */}
                  <div className="lg:col-span-2">
                    <h3 className="text-xl font-bold text-slate-800 mb-4">🔍 {t('mentalHealth.reportDetail.hrvMetrics')}</h3>
                    {report.mindScore.components && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* 심박수 카드 */}
                        <div className="relative bg-gradient-to-br from-red-100 to-pink-100 rounded-2xl p-5 shadow-lg border border-white/50 overflow-hidden">
                          <div className="absolute -top-4 -right-4 w-24 h-24 bg-red-200/50 rounded-full"></div>
                          <div className="relative">
                            <div className="text-2xl mb-3">💓</div>
                            <div className="flex justify-between items-baseline">
                              <span className="font-semibold text-red-800">{t('mentalHealth.reportDetail.heartRate')}</span>
                              <span className="text-3xl font-extrabold text-red-900">{Math.round(report.mindScore.components.HR_med)}</span>
                            </div>
                            <p className="text-sm text-red-700 mt-1">{t('mentalHealth.reportDetail.bpm')}</p>
                          </div>
                        </div>
                        {/* RMSSD 카드 */}
                        <div className="relative bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl p-5 shadow-lg border border-white/50 overflow-hidden">
                          <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-200/50 rounded-full"></div>
                          <div className="relative">
                            <div className="text-2xl mb-3">📊</div>
                            <div className="flex justify-between items-baseline">
                              <span className="font-semibold text-blue-800">{t('mentalHealth.reportDetail.rmssd')}</span>
                              <span className="text-3xl font-extrabold text-blue-900">{report.mindScore.components?.RMSSD_med?.toFixed(1) || 'N/A'}</span>
                            </div>
                            <p className="text-sm text-blue-700 mt-1">{t('mentalHealth.reportDetail.unit.ms')}</p>
                          </div>
                        </div>
                        {/* 호흡수 카드 */}
                        <div className="relative bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl p-5 shadow-lg border border-white/50 overflow-hidden">
                          <div className="absolute -top-4 -right-4 w-24 h-24 bg-emerald-200/50 rounded-full"></div>
                          <div className="relative">
                            <div className="text-2xl mb-3">🫁</div>
                            <div className="flex justify-between items-baseline">
                              <span className="font-semibold text-emerald-800">{t('mentalHealth.reportDetail.respiration')}</span>
                              <span className="text-3xl font-extrabold text-emerald-900">{report.mindScore.components?.Resp_med?.toFixed(1) || 'N/A'}</span>
                            </div>
                            <p className="text-sm text-emerald-700 mt-1">{t('mentalHealth.reportDetail.rpm')}</p>
                          </div>
                        </div>
                        {/* 스트레스/균형 카드 */}
                        <div className="relative bg-gradient-to-br from-amber-100 to-yellow-100 rounded-2xl p-5 shadow-lg border border-white/50 overflow-hidden">
                          <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-200/50 rounded-full"></div>
                          <div className="relative">
                            <div className="text-2xl mb-3">⚡</div>
                            <div className="flex justify-between items-baseline">
                              <span className="font-semibold text-amber-800">{t('mentalHealth.reportDetail.stressLevel')}</span>
                              <span className={`text-2xl font-extrabold text-amber-900`}>{t(`mentalHealth.stress.${report.stressAnalysis.category}`)}</span>
                            </div>
                            <p className="text-sm text-amber-700 mt-1">{translateValue(report.balanceAnalysis.flag, 'mentalHealth.balance')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 전문가 해석 */}
            <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-8 py-6">
                <h3 className="text-2xl font-bold text-white flex items-center">
                  👨‍⚕️ {t('mentalHealth.reportDetail.interpretation')} & {t('mentalHealth.reportDetail.recommendation')}
                </h3>
                <p className="text-blue-100 mt-2">{t('mentalHealth.reportDetail.hrvBased')}</p>
              </div>

              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* 현재 상태 분석 */}
                  <div className="space-y-6">
                    <div className="border-l-4 border-blue-500 pl-6">
                      <h4 className="text-lg font-bold text-slate-800 mb-3">📋 {t('mentalHealth.reportDetail.overallHealth')}</h4>
                      <div className="bg-blue-50 rounded-xl p-4 mb-4">
                        <p className="text-slate-700 leading-relaxed">
                          {translateValue(report.stressAnalysis.description)}
                        </p>
                      </div>
                      <div className="bg-indigo-50 rounded-xl p-4">
                        <p className="text-slate-700 leading-relaxed">
                          {translateValue(report.balanceAnalysis.description)}
                        </p>
                      </div>
                    </div>

                    {/* 데이터 품질 */}
                    <div className="border-l-4 border-emerald-500 pl-6">
                      <h4 className="text-lg font-bold text-slate-800 mb-3">📊 {t('mentalHealth.reportDetail.dataQuality')}</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">{t('mentalHealth.reportDetail.duration')}</span>
                          <span className="font-semibold text-slate-800">
                            {report.qualityMetrics?.duration_min?.toFixed(1) || 'N/A'}{t('mentalHealth.reportDetail.minutes')}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">{t('common.confidence')}</span>
                          <div className="flex items-center">
                            <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className="bg-emerald-500 h-2 rounded-full"
                                style={{ width: `${report.qualityMetrics.analyzability * 100}%` }}
                              ></div>
                            </div>
                            <span className="font-semibold text-slate-800">
                              {((report.qualityMetrics?.analyzability || 0) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 맞춤형 권고사항 */}
                  <div className="space-y-6">
                    <div className="border-l-4 border-amber-500 pl-6">
                      <h4 className="text-lg font-bold text-slate-800 mb-3">💡 {t('mentalHealth.reportDetail.recommendation')}</h4>
                      <div className="space-y-3">
                        {report.mindScore.score >= 80 ? (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                            <div className="flex items-start">
                              <div className="text-2xl mr-3">🎉</div>
                              <div>
                                <h5 className="font-bold text-emerald-800 mb-2">{t('mentalHealth.detail.excellentHealth')}</h5>
                                <ul className="text-sm text-emerald-700 space-y-1">
                                  <li>• {t('mentalHealth.detail.excellentHealthDesc1')}</li>
                                  <li>• {t('mentalHealth.detail.excellentHealthDesc2')}</li>
                                  <li>• {t('mentalHealth.detail.excellentHealthDesc3')}</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        ) : report.mindScore.score >= 70 ? (
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <div className="flex items-start">
                              <div className="text-2xl mr-3">👍</div>
                              <div>
                                <h5 className="font-bold text-blue-800 mb-2">{t('mentalHealth.detail.goodState')}</h5>
                                <ul className="text-sm text-blue-700 space-y-1">
                                  <li>• {t('mentalHealth.detail.goodStateDesc1')}</li>
                                  <li>• {t('mentalHealth.detail.goodStateDesc2')}</li>
                                  <li>• {t('mentalHealth.detail.goodStateDesc3')}</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        ) : report.mindScore.score >= 55 ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <div className="flex items-start">
                              <div className="text-2xl mr-3">⚠️</div>
                              <div>
                                <h5 className="font-bold text-amber-800 mb-2">{t('mentalHealth.detail.cautionNeeded')}</h5>
                                <ul className="text-sm text-amber-700 space-y-1">
                                  <li>• {t('mentalHealth.detail.cautionDesc1')}</li>
                                  <li>• {t('mentalHealth.detail.cautionDesc2')}</li>
                                  <li>• {t('mentalHealth.detail.cautionDesc3')}</li>
                                  <li>• {t('mentalHealth.detail.cautionDesc4')}</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                            <div className="flex items-start">
                              <div className="text-2xl mr-3">🚨</div>
                              <div>
                                <h5 className="font-bold text-red-800 mb-2">{t('mentalHealth.detail.consultationRecommended')}</h5>
                                <ul className="text-sm text-red-700 space-y-1">
                                  <li>• {t('mentalHealth.detail.consultDesc1')}</li>
                                  <li>• {t('mentalHealth.detail.consultDesc2')}</li>
                                  <li>• {t('mentalHealth.detail.consultDesc3')}</li>
                                  <li>• {t('mentalHealth.detail.consultDesc4')}</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 생활 습관 가이드 */}
                    <div className="border-l-4 border-purple-500 pl-6">
                      <h4 className="text-lg font-bold text-slate-800 mb-3">🌱 {t('mentalHealth.detail.lifestyleGuide')}</h4>
                      <div className="bg-purple-50 rounded-xl p-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="font-semibold text-purple-800 mb-2">{t('mentalHealth.detail.recommendedActions')}</div>
                            <ul className="text-purple-700 space-y-1">
                              <li>• {t('mentalHealth.detail.regularSleep')}</li>
                              <li>• {t('mentalHealth.detail.moderateExercise')}</li>
                              <li>• {t('mentalHealth.detail.balancedDiet')}</li>
                            </ul>
                          </div>
                          <div>
                            <div className="font-semibold text-purple-800 mb-2">{t('mentalHealth.detail.precautions')}</div>
                            <ul className="text-purple-700 space-y-1">
                              <li>• {t('mentalHealth.detail.excessiveCaffeine')}</li>
                              <li>• {t('mentalHealth.detail.irregularLifestyle')}</li>
                              <li>• {t('mentalHealth.detail.chronicStress')}</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 마음 건강 지수 */}
            <div className="bg-white rounded-2xl border-2 border-gray-100 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('mentalHealth.detail.mindHealthIndex')}</h3>

              {/* 지수 아이콘들 */}
              <div className="flex justify-center space-x-8 mb-8">
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl mb-2 ${report.balanceAnalysis.flag === 'normal' ? 'bg-blue-100' : 'bg-yellow-100'
                    }`}>
                    😊
                  </div>
                  <div className="text-sm font-medium text-gray-700">{t('mentalHealth.detail.autonomicActivity')}</div>
                </div>

                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl mb-2 ${report.balanceAnalysis.flag === 'normal' ? 'bg-green-100' : 'bg-orange-100'
                    }`}>
                    {report.balanceAnalysis.flag === 'normal' ? '😊' : '😟'}
                  </div>
                  <div className="text-sm font-medium text-gray-700">{t('mentalHealth.detail.autonomicBalance')}</div>
                </div>

                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl mb-2 ${report.stressAnalysis.category === 'low' ? 'bg-green-100' :
                    report.stressAnalysis.category === 'normal' ? 'bg-yellow-100' : 'bg-red-100'
                    }`}>
                    {report.stressAnalysis.category === 'low' ? '😊' :
                      report.stressAnalysis.category === 'normal' ? '😐' : '😰'}
                  </div>
                  <div className="text-sm font-medium text-gray-700">{t('mentalHealth.detail.stressState')}</div>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-2xl mb-2">
                    😄
                  </div>
                  <div className="text-sm font-medium text-gray-700">{t('mentalHealth.detail.subjectivePsychology')}</div>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-2xl mb-2">
                    😔
                  </div>
                  <div className="text-sm font-medium text-gray-700">{t('mentalHealth.detail.risk')}</div>
                </div>
              </div>

              {/* 상세 지수 카드들 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6 text-center">
                  <div className="text-4xl mb-3">😟</div>
                  <div className="text-lg font-bold text-orange-800 mb-2">{t('mentalHealth.detail.autonomicBalance')}</div>
                  <div className="text-2xl font-bold text-orange-900">{t(`mentalHealth.balance.${report.balanceAnalysis.flag}`)}</div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-6 text-center">
                  <div className="text-4xl mb-3">😐</div>
                  <div className="text-lg font-bold text-yellow-800 mb-2">{t('mentalHealth.detail.stressStateLabel')}</div>
                  <div className="text-2xl font-bold text-yellow-900">{t(`mentalHealth.stress.${report.stressAnalysis.category.toLowerCase()}`)}</div>
                </div>
              </div>
            </div>


            {/* 마음 건강 점수 상세 */}
            <div className="bg-white rounded-2xl border-2 border-gray-100 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('mentalHealth.reportDetail.mindScoreDetail')}</h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 왼쪽: 점수와 설명 */}
                <div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                    <h4 className="text-lg font-bold text-blue-900 mb-3">{t('mentalHealth.reportDetail.whatIsMindScore')}</h4>
                    <p className="text-blue-800 leading-relaxed">
                      {t('mentalHealth.reportDetail.mindScoreDesc')}
                    </p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-green-900 mb-3">{t('mentalHealth.reportDetail.autonomicRelation')}</h4>
                    <p className="text-green-800 leading-relaxed">
                      {t('mentalHealth.reportDetail.autonomicRelationDesc')}
                    </p>
                  </div>
                </div>

                {/* 오른쪽: 점수 구성 요소 */}
                <div>
                  <div className="text-center mb-6">
                    <div className="text-6xl font-bold text-blue-600 mb-2">
                      {Math.round(report.mindScore?.score || 0)}
                    </div>
                    <div className="text-lg text-gray-600">{t('mentalHealth.score.points')}</div>
                    <div className="text-sm text-gray-500 mt-2">
                      • {t('mentalHealth.reportDetail.dataQuality')}: {translateValue(report.decisionLabel, 'mentalHealth.status')}
                    </div>
                    <div className="text-sm text-gray-500">
                      • {t('mentalHealth.detail.average')}: {report.mindScore.components?.HR_med?.toFixed(0) || 87} bpm
                    </div>
                  </div>

                  {report.mindScore.components && (
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700 font-medium">{t('mentalHealth.reportDetail.heartRate')}</span>
                          <span className="text-lg font-bold text-gray-900">
                            {report.mindScore.components?.HR_med?.toFixed(0) || 'N/A'} bpm
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {t('mentalHealth.reportDetail.score')}: {report.mindScore.components?.s_HR?.toFixed(1) || 'N/A'}{t('mentalHealth.score.points')}
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700 font-medium">{t('mentalHealth.reportDetail.rmssd')}</span>
                          <span className="text-lg font-bold text-gray-900">
                            {report.mindScore.components?.RMSSD_med?.toFixed(1) || 'N/A'} ms
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {t('mentalHealth.reportDetail.score')}: {report.mindScore.components?.s_RMSSD?.toFixed(1) || 'N/A'}{t('mentalHealth.score.points')}
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700 font-medium">{t('mentalHealth.reportDetail.respiration')}</span>
                          <span className="text-lg font-bold text-gray-900">
                            {report.mindScore.components?.Resp_med?.toFixed(1) || 'N/A'} bpm
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {t('mentalHealth.reportDetail.score')}: {report.mindScore.components?.s_Resp?.toFixed(1) || 'N/A'}{t('mentalHealth.score.points')}
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700 font-medium">{t('mentalHealth.reportDetail.duration')}</span>
                          <span className="text-lg font-bold text-gray-900">
                            {report.qualityMetrics?.duration_min?.toFixed(1) || 'N/A'} {t('mentalHealth.reportDetail.minutes')}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {t('mentalHealth.reportDetail.dataQuality')}: {((report.qualityMetrics?.analyzability || 0) * 100).toFixed(0)}%
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 자율신경 활성도 */}
            <div className="bg-white rounded-2xl border-2 border-gray-100 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('mentalHealth.detail.ansActivity')}</h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 왼쪽: 상태 표시 */}
                <div className="flex flex-col items-center">
                  <div className="bg-yellow-100 border-2 border-yellow-300 rounded-2xl p-6 w-full max-w-sm mb-6">
                    <div className="flex items-center justify-center mb-4">
                      <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center text-2xl">
                        😊
                      </div>
                      <div className="ml-4">
                        <div className="text-2xl font-bold text-yellow-800">{t('mentalHealth.detail.autonomicActivityModerate')}</div>
                        <div className="text-sm text-yellow-700">{t('mentalHealth.detail.ansActivityNormalDesc')}</div>
                      </div>
                    </div>
                  </div>

                  {/* HRV 파워 차트 */}
                  <div className="w-full">
                    <div className="flex justify-between items-end space-x-4 h-40 mb-4">
                      <div className="flex flex-col items-center">
                        <div
                          className="bg-blue-500 rounded-t-lg w-16 flex items-end justify-center text-white font-bold text-sm pb-2"
                          style={{ height: `${(report.psdData.TP / 3000) * 100}%` }}
                        >
                          {Math.round(report.psdData.TP / 100)}
                        </div>
                        <div className="text-xs text-gray-600 mt-2 text-center">TP</div>
                      </div>

                      <div className="flex flex-col items-center">
                        <div
                          className="bg-yellow-500 rounded-t-lg w-16 flex items-end justify-center text-white font-bold text-sm pb-2"
                          style={{ height: `${(report.psdData.VLF / 1000) * 100}%` }}
                        >
                          {Math.round(report.psdData.VLF / 10)}
                        </div>
                        <div className="text-xs text-gray-600 mt-2 text-center">VLF</div>
                      </div>

                      <div className="flex flex-col items-center">
                        <div
                          className="bg-red-500 rounded-t-lg w-16 flex items-end justify-center text-white font-bold text-sm pb-2"
                          style={{ height: `${(report.psdData.LF / 1000) * 100}%` }}
                        >
                          {Math.round(report.psdData.LF / 10)}
                        </div>
                        <div className="text-xs text-gray-600 mt-2 text-center">LF</div>
                      </div>

                      <div className="flex flex-col items-center">
                        <div
                          className="bg-green-500 rounded-t-lg w-16 flex items-end justify-center text-white font-bold text-sm pb-2"
                          style={{ height: `${(report.psdData.HF / 1000) * 100}%` }}
                        >
                          {Math.round(report.psdData.HF / 10)}
                        </div>
                        <div className="text-xs text-gray-600 mt-2 text-center">HF</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 text-center">
                      {t('mentalHealth.detail.unitMs2LogNote')}
                    </div>
                  </div>
                </div>

                {/* 오른쪽: 상세 설명 */}
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
                      <span className="font-semibold text-blue-800">{t('mentalHealth.detail.totalAnsActivity')}</span>
                    </div>
                    <p className="text-sm text-blue-700">
                      {t('mentalHealth.detail.totalAnsActivityDesc')}
                    </p>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <div className="w-4 h-4 bg-yellow-500 rounded mr-2"></div>
                      <span className="font-semibold text-yellow-800">{t('mentalHealth.detail.vlfDesc')}</span>
                    </div>
                    <p className="text-sm text-yellow-700">
                      {t('mentalHealth.detail.sympatheticActivityDesc')}
                    </p>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                      <span className="font-semibold text-red-800">{t('mentalHealth.detail.lfDesc')}</span>
                    </div>
                    <p className="text-sm text-red-700">
                      {t('mentalHealth.detail.lfActivityDesc')}
                    </p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                      <span className="font-semibold text-green-800">{t('mentalHealth.detail.hfDesc')}</span>
                    </div>
                    <p className="text-sm text-green-700">
                      {t('mentalHealth.detail.hfActivityDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 자율신경 균형도 */}
            <div className="bg-white rounded-2xl border-2 border-gray-100 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('mentalHealth.detail.ansBalance')}</h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* 왼쪽: 상태 표시 */}
                <div className="flex flex-col items-center">
                  <div className="bg-orange-100 border-2 border-orange-300 rounded-2xl p-6 w-full max-w-sm mb-6">
                    <div className="flex items-center justify-center mb-4">
                      <div className="w-16 h-16 bg-orange-400 rounded-full flex items-center justify-center text-2xl">
                        😟
                      </div>
                      <div className="ml-4">
                        <div className="text-2xl font-bold text-orange-800">{t('mentalHealth.detail.ansBalanceCautionLabel')}</div>
                        <div className="text-sm text-orange-700">
                          {t('mentalHealth.detail.ansBalanceCautionDesc')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SNS/PNS 균형 차트 */}
                  <div className="w-full space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">SNS</span>
                        <span className="text-lg font-bold text-pink-600">
                          {report.psdData?.LFnu?.toFixed(1) || 'N/A'}
                        </span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-6">
                        <div
                          className="bg-gradient-to-r from-pink-400 to-pink-600 h-6 rounded-full flex items-center justify-center"
                          style={{ width: `${report.psdData.LFnu}%` }}
                        >
                          <span className="text-white text-xs font-bold">
                            {report.psdData?.LFnu?.toFixed(1) || '0'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">PNS</span>
                        <span className="text-lg font-bold text-blue-600">
                          {report.psdData?.HFnu?.toFixed(1) || 'N/A'}
                        </span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-6">
                        <div
                          className="bg-gradient-to-r from-blue-400 to-blue-600 h-6 rounded-full flex items-center justify-center"
                          style={{ width: `${report.psdData.HFnu}%` }}
                        >
                          <span className="text-white text-xs font-bold">
                            {report.psdData?.HFnu?.toFixed(1) || '0'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 오른쪽: 상세 설명 */}
                <div className="space-y-4">
                  <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <div className="w-4 h-4 bg-pink-500 rounded mr-2"></div>
                      <span className="font-semibold text-pink-800">{t('mentalHealth.detail.sympatheticLabel')}</span>
                    </div>
                    <p className="text-sm text-pink-700">
                      {t('mentalHealth.detail.sympatheticBalanceDesc')}
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
                      <span className="font-semibold text-blue-800">
                        {t('mentalHealth.detail.parasympatheticLabel')}
                      </span>
                    </div>
                    <p className="text-sm text-blue-700">
                      {t('mentalHealth.detail.parasympatheticBalanceDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 종합 해석 카드 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 마음건강 종합 평가 */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white text-xl">🧠</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-blue-900">{t('mentalHealth.detail.comprehensiveHealthAssessment')}</h3>
                    <p className="text-sm text-blue-700">{t('mentalHealth.detail.bcgBasedAnalysis')}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-white/70 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-blue-800 font-medium">{t('mentalHealth.detail.mindHealthScore')}</span>
                      <span className="text-2xl font-bold text-blue-900">
                        {report.mindScore?.score?.toFixed(1) || '0.0'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${report.mindScore.score >= 80 ? 'bg-green-500' :
                          report.mindScore.score >= 70 ? 'bg-blue-500' :
                            report.mindScore.score >= 55 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                        style={{ width: `${Math.min(report.mindScore.score, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-blue-700 mt-2">
                      {t('mentalHealth.detail.statusLabel')}: <span className="font-semibold">{report.decisionLabel}</span>
                    </p>
                  </div>

                  <div className="bg-white/70 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">{t('mentalHealth.detail.keyIndicators')}</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-blue-700">{t('mentalHealth.detail.stressLabel')}:</span>
                        <span className="ml-1 font-medium">{t(`mentalHealth.stress.${report.stressAnalysis.category}`)}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">{t('mentalHealth.detail.autonomicNervousSystem')}:</span>
                        <span className="ml-1 font-medium">{translateValue(report.balanceAnalysis.flag, 'mentalHealth.balance')}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">{t('mentalHealth.detail.respirationStateLabel')}:</span>
                        <span className="ml-1 font-medium">{translateValue(report.respirationState, 'mentalHealth.respiration')}</span>
                      </div>
                      <div>
                        <span className="text-blue-700">{t('mentalHealth.detail.dataQualityLabel')}:</span>
                        <span className="ml-1 font-medium">
                          {((report.qualityMetrics?.analyzability || 0) * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 권고사항 및 해석 */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 border border-green-200 rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white text-xl">💡</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-green-900">{t('mentalHealth.detail.interpretationRecommendations')}</h3>
                    <p className="text-sm text-green-700">{t('mentalHealth.detail.personalizedGuide')}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-white/70 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">🎯 {t('mentalHealth.detail.currentStateInterpretation')}</h4>
                    <p className="text-sm text-green-800 leading-relaxed">
                      {translateValue(report.stressAnalysis.description)}
                    </p>
                  </div>

                  <div className="bg-white/70 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">⚖️ {t('mentalHealth.detail.autonomicNervousSystemAnalysis')}</h4>
                    <p className="text-sm text-green-800 leading-relaxed">
                      {translateValue(report.balanceAnalysis.description)}
                    </p>
                  </div>

                  <div className="bg-white/70 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">📋 {t('mentalHealth.detail.recommendationsLabel')}</h4>
                    <div className="text-sm text-green-800 space-y-2">
                      {report.mindScore.score >= 80 ? (
                        <div className="space-y-1">
                          <p>✅ {t('mentalHealth.detail.recommendation.excellent1')}</p>
                          <p>✅ {t('mentalHealth.detail.recommendation.excellent2')}</p>
                          <p>✅ {t('mentalHealth.detail.recommendation.excellent3')}</p>
                        </div>
                      ) : report.mindScore.score >= 70 ? (
                        <div className="space-y-1">
                          <p>✅ {t('mentalHealth.detail.recommendation.good1')}</p>
                          <p>⚠️ {t('mentalHealth.detail.recommendation.good2')}</p>
                          <p>💪 {t('mentalHealth.detail.recommendation.good3')}</p>
                        </div>
                      ) : report.mindScore.score >= 55 ? (
                        <div className="space-y-1">
                          <p>⚠️ {t('mentalHealth.detail.recommendation.caution1')}</p>
                          <p>🧘 {t('mentalHealth.detail.recommendation.caution2')}</p>
                          <p>😴 {t('mentalHealth.detail.recommendation.caution3')}</p>
                          <p>🏥 {t('mentalHealth.detail.recommendation.caution4')}</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p>🚨 {t('mentalHealth.detail.recommendation.risk1')}</p>
                          <p>🏥 {t('mentalHealth.detail.recommendation.risk2')}</p>
                          <p>💊 {t('mentalHealth.detail.recommendation.risk3')}</p>
                          <p>👥 {t('mentalHealth.detail.recommendation.risk4')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* HRV 상세 분석 */}
            <div className="bg-white border rounded-xl p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-lg">📊</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{t('mentalHealth.detail.hrvDetailedAnalysis')}</h3>
                  <p className="text-sm text-gray-600">{t('mentalHealth.detail.autonomicActivityAnalysis')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-700">
                      {report.psdData?.VLF?.toFixed(0) || 'N/A'}
                    </div>
                    <div className="text-sm text-red-600 font-medium">VLF Power</div>
                    <div className="text-xs text-red-500 mt-1">{t('mentalHealth.detail.veryLowFrequency')}</div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-700">
                      {report.psdData?.LF?.toFixed(0) || 'N/A'}
                    </div>
                    <div className="text-sm text-orange-600 font-medium">LF Power</div>
                    <div className="text-xs text-orange-500 mt-1">{t('mentalHealth.detail.lowFrequencySympathetic')}</div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-700">
                      {report.psdData?.HF?.toFixed(0) || 'N/A'}
                    </div>
                    <div className="text-sm text-blue-600 font-medium">HF Power</div>
                    <div className="text-xs text-blue-500 mt-1">{t('mentalHealth.detail.highFrequencyParasympathetic')}</div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-700">
                      {report.psdData?.LF_HF?.toFixed(2) || 'N/A'}
                    </div>
                    <div className="text-sm text-purple-600 font-medium">{t('mentalHealth.detail.lfhfRatio')}</div>
                    <div className="text-xs text-purple-500 mt-1">{t('mentalHealth.detail.autonomicBalanceShort')}</div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">📈 {t('mentalHealth.detail.hrvInterpretation')}</h4>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>• <strong>{t('mentalHealth.detail.lfhfRatio')}:</strong> {
                    report.psdData.LF_HF > 2.5 ? t('mentalHealth.detail.sympatheticDominantStress') :
                      report.psdData.LF_HF < 0.5 ? t('mentalHealth.detail.parasympatheticDominantExcessiveRelaxation') :
                        t('mentalHealth.detail.autonomicBalanceState')
                  }</p>
                  <p>• <strong>{t('mentalHealth.detail.totalPower')}:</strong> {report.psdData?.TP?.toFixed(0) || 'N/A'} ms²</p>
                  <p>• <strong>{t('mentalHealth.detail.normalizedLF')}:</strong> {report.psdData?.LFnu?.toFixed(1) || 'N/A'}%</p>
                  <p>• <strong>{t('mentalHealth.detail.normalizedHF')}:</strong> {report.psdData?.HFnu?.toFixed(1) || 'N/A'}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'symptoms' && (
          <div className="space-y-6">
            {/* 헤더 */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-8 text-white">
              <div className="flex items-center space-x-4 mb-4">
                <div className="text-6xl">🏥</div>
                <div>
                  <h2 className="text-3xl font-bold">{t('mentalHealth.detail.symptomPredictionTitle')}</h2>
                  <p className="text-blue-100 mt-2">{t('mentalHealth.detail.symptomPredictionSubtitle')}</p>
                </div>
              </div>
              <div className="bg-blue-500/30 rounded-xl p-4 mt-4">
                <p className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: t('mentalHealth.detail.symptomNote') }} />
              </div>
            </div>

            {report.symptomPredictions ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SymptomPredictionCard prediction={report.symptomPredictions.stress} />
                <SymptomPredictionCard prediction={report.symptomPredictions.breathing} />
                <SymptomPredictionCard prediction={report.symptomPredictions.pain} />
                <SymptomPredictionCard prediction={report.symptomPredictions.depression} />
                <SymptomPredictionCard prediction={report.symptomPredictions.cardiovascular} />
                <SymptomPredictionCard prediction={report.symptomPredictions.bloodPressure} />
              </div>
            ) : (
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-8 text-center">
                <div className="text-6xl mb-4">⚠️</div>
                <h3 className="text-xl font-bold text-yellow-800 mb-2">{t('mentalHealth.detail.symptomPredictionNoData')}</h3>
                <p className="text-yellow-700">
                  {t('mentalHealth.detail.symptomPredictionOldReport')}
                </p>
              </div>
            )}

            {/* 증상별 설명 */}
            <div className="bg-white rounded-3xl border border-gray-200 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">📚 {t('mentalHealth.detail.symptomIndicatorGuide')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-2">🧠 {t('mentalHealth.symptom.stress')}</h4>
                  <p className="text-sm text-gray-700">
                    {t('mentalHealth.detail.stressIndicatorDesc')}
                  </p>
                </div>
                <div className="border-l-4 border-cyan-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-2">🫁 {t('mentalHealth.symptom.breathing')}</h4>
                  <p className="text-sm text-gray-700">
                    {t('mentalHealth.detail.breathingIndicatorDesc')}
                  </p>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-2">😣 {t('mentalHealth.symptom.pain')}</h4>
                  <p className="text-sm text-gray-700">
                    {t('mentalHealth.detail.painIndicatorDesc')}
                  </p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-2">😔 {t('mentalHealth.symptom.depression')}</h4>
                  <p className="text-sm text-gray-700">
                    {t('mentalHealth.detail.depressionIndicatorDesc')}
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-2">❤️ {t('mentalHealth.symptom.cardiovascular')}</h4>
                  <p className="text-sm text-gray-700">
                    {t('mentalHealth.detail.cardiovascularIndicatorDesc')}
                  </p>
                </div>
                <div className="border-l-4 border-pink-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-2">💉 {t('mentalHealth.symptom.bloodPressure')}</h4>
                  <p className="text-sm text-gray-700">
                    {t('mentalHealth.detail.bloodPressureIndicatorDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'charts' && report.mindScore.selectedWindows && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <VitalSignsChart
                windows={report.mindScore.selectedWindows}
                type="hr"
              />
              <VitalSignsChart
                windows={report.mindScore.selectedWindows}
                type="rmssd"
              />
              <VitalSignsChart
                windows={report.mindScore.selectedWindows}
                type="resp"
              />
              <VitalSignsChart
                windows={report.mindScore.selectedWindows}
                type="dqi"
              />
            </div>
          </div>
        )}

        {activeTab === 'raw' && (
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-semibold mb-4">{t('mentalHealth.detail.rawData')}</h3>
            <pre className="bg-gray-50 p-4 rounded text-sm overflow-auto max-h-96">
              {JSON.stringify(report, null, 2)}
            </pre>
          </div>
        )}

        {/* 주의사항 */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800" dangerouslySetInnerHTML={{ __html: t('mentalHealth.detail.rawDataNote') }} />
        </div>
      </div>
    </PageConfig>
  );
};

export default MentalHealthReportDetail;
